/**
 * 機能をまたぐルートの集合と、その集合による判定（どのルートが PJ・開く操作を受けるか）。
 *
 * 集合は機能ごとのパス定義（`paths/*.ts`）が出す部分（`*RouteSets`）を並べて作るだけである。
 * どのルートがどの集合に入るかは機能の側が自分のファイルで決め、ここは機能をまたいで結合する。
 * 機能がルートを足すときに、このファイルへ行を足さずに済む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 機能をまたぐルートの集合（全ルート・PJ を受けるルート・操作を受けるルート・PJ モードの現在地）と判定を定義する。
 * @graph-connects none
 */
import { businessLogicRouteSets } from './business-logic.js';
import { businessMapRouteSets } from './business-map.js';
import { commonRouteSets } from './common.js';
import { flowRouteSets } from './flow.js';
import { mastersRouteSets } from './masters.js';
import { pjRouteSets } from './pj.js';

/**
 * ナビゲーションが指すルートの一覧。「実在するルート」と「ナビの行き先」がずれていない
 * ことをテストで突合するための、定数の集合表現である。
 * @graph-connects none
 */
export const ALL_ROUTES = [
  ...commonRouteSets.all,
  ...pjRouteSets.all,
  ...businessMapRouteSets.all,
  ...flowRouteSets.all,
  ...businessLogicRouteSets.all,
  ...mastersRouteSets.all,
] as const;

/**
 * PJ を検索条件（`?pj=`）で受けるルートの一覧。
 *
 * **これは「PJ を渡してよい相手」の集合である。** 集合を持たずに「一覧と詳細には付ける」と
 * 書き下すと、付ける側（サイドバー・行のリンク）と読む側（検証器を張ったルート）が別々に育ち、
 * 渡しているのに読まない組み合わせができる。実際それが起きた — PJ 一覧（`/pjs`）は PJ を
 * 読まないのに `?pj=` を渡してしまい、PJ を適用していない PJ 選択画面に「PJ を適用した
 * 後の姿を見ています」という案内が出ていた。
 *
 * **PJ をパスの親に置くルート（`/pjs/$pjNumber/...`）はここに入れない。** そちらは
 * PJ が URL の構造そのものなので、検索条件で重ねる相手ではない。**PJ モードとして扱ってよい
 * 現在地はこの集合ではなく `PJ_MODE_ROUTES`（両方の置き場を合わせたもの）である。**
 *
 * この集合とルート定義（`registry/*`）の `validateSearch` の突合はテストが持つ。集合に足したのに
 * ルートへ検証器を張り忘れると、渡した PJ が黙って捨てられる。
 * @graph-connects none
 */
export const PJ_SEARCH_ROUTES = [
  ...commonRouteSets.pjSearch,
  ...pjRouteSets.pjSearch,
  ...businessMapRouteSets.pjSearch,
  ...flowRouteSets.pjSearch,
  ...businessLogicRouteSets.pjSearch,
  ...mastersRouteSets.pjSearch,
] as const;

/** PJ を検索条件で受けるルート。 */
export type PjSearchRoute = (typeof PJ_SEARCH_ROUTES)[number];

/**
 * 開く操作を検索条件（`?op=`）で受けるルートの一覧。
 *
 * **これは「操作を渡してよい相手」の集合である。** PJ を受ける集合（`PJ_SEARCH_ROUTES`）
 * とは別に持つ。PJ と操作は別の条件で、置き場も違う — PJ はパスにも現れるが、操作は必ず
 * 検索条件である。1 つの集合に混ぜると「PJ を渡してよい相手」の意味が濁り、PJ を読まない
 * ルートへ PJ を渡す取りこぼしが再び作れるようになる。
 *
 * 操作を受けるのは業務フローの図の 2 本だけである。操作の中身は図の節点を選んで読むもので、図を
 * 持たない画面には開く先が無い。業務詳細（データの流れの図）は図を持つが、図の中の選択を URL に
 * 写さないので `op` を受けない（`parseDataflowSearch`）。
 *
 * この集合とルート定義（`registry/*`）の `validateSearch` の突合はテストが持つ。集合に足したのに
 * ルートへ検証器を張り忘れると、渡した操作が黙って捨てられる。
 * @graph-connects none
 */
export const OPERATION_SEARCH_ROUTES = [
  ...commonRouteSets.operationSearch,
  ...pjRouteSets.operationSearch,
  ...businessMapRouteSets.operationSearch,
  ...flowRouteSets.operationSearch,
  ...businessLogicRouteSets.operationSearch,
  ...mastersRouteSets.operationSearch,
] as const;

/** 開く操作を検索条件で受けるルート。 */
export type OperationSearchRoute = (typeof OPERATION_SEARCH_ROUTES)[number];

/**
 * そのパスが PJ を検索条件で受けるかを判定する。
 *
 * @param path 判定するパス
 * @returns PJ を渡してよいなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスが PJ を検索条件で受けるルートかを判定する。
 * @graph-connects none
 */
export function readsPjSearch(path: string): path is PjSearchRoute {
  return (PJ_SEARCH_ROUTES as readonly string[]).includes(path);
}

/**
 * PJ をパスの親（`/pjs/$pjNumber/...`）に置くルートの一覧。
 *
 * 検索条件で PJ を受けるルートと分けて持つのは、**PJ を外したときの行き先が違う**からである。
 * 検索条件のほうは同じ画面から条件だけを落とせばよいが、こちらは PJ が URL の構造なので、
 * 落とすと URL そのものが成立しない（`pjExitTarget`）。
 * @graph-connects none
 */
export const PJ_PATH_ROUTES = [
  ...commonRouteSets.pjPath,
  ...pjRouteSets.pjPath,
  ...businessMapRouteSets.pjPath,
  ...flowRouteSets.pjPath,
  ...businessLogicRouteSets.pjPath,
  ...mastersRouteSets.pjPath,
] as const;

/**
 * PJ モードとして扱ってよい現在地の一覧。PJ が URL の**どちらの置き場に居ても**入る。
 *
 * **PJ を読む画面すべてでヘッダーに PJ を出す。** 検索条件で受けるルートだけを見ていたときは、
 * PJ モードの入口である PJ サマリー（`/pjs/$pjNumber`）と PJ から見たフローで案内が
 * 出ず、PJ を選んだ直後の画面が「PJ を選んでいない画面」と同じ見た目になっていた。PJ を
 * 選ぶ操作の起点で状態が見えない、という一番効く場所での取りこぼしである。
 *
 * **画面の一覧を第 2 の場所に書き下さず、置き場ごとの集合から導く。** 書き下すと、ルートを
 * 足したときに `validateSearch` を張る側とヘッダーに出す側が別々に育ち、PJ を読むのに PJ
 * モードだと画面に出ないルートができる（core-principles の一次表現原則）。
 * @graph-connects none
 */
export const PJ_MODE_ROUTES = [...PJ_SEARCH_ROUTES, ...PJ_PATH_ROUTES] as const;

/** PJ モードとして扱ってよい現在地。 */
export type PjModeRoute = (typeof PJ_MODE_ROUTES)[number];

/**
 * そのパスが PJ を読むルートかを判定する。
 *
 * **PJ 番号があることと、PJ モードであることは同じではない。** PJ を読まない画面
 * （PJ 一覧など）でも URL に `?pj=` は手で書ける。番号の有無だけで案内を出すと、PJ を
 * 適用していない画面が「PJ を適用した後の姿」だと読める。
 *
 * @param path 判定するパス
 * @returns PJ モードとして扱ってよい現在地なら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスが PJ を読むルート（PJ モードとして扱ってよい現在地）かを判定する。
 * @graph-connects none
 */
export function readsPj(path: string): path is PjModeRoute {
  return (PJ_MODE_ROUTES as readonly string[]).includes(path);
}
